import {
  AGGRID_AUTOCOLUMN_ID,
  HamburgerMenu,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  useDynamicCallback,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  type Column,
  type ColumnDef,
  type CreditBlotterExposure,
  type Exposure,
  type FilterableProperty,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { colIDToFilterBuilderKey } from './useCreditFilter';

export function useCreditMenu({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}): {
  column: Column;
  getContextMenuItems: (params: GetContextMenuItemsParams) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const getContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams) => {
    const exposure = params.node?.data;
    const isTreeGroupingColumn = params.column?.getColId() === AGGRID_AUTOCOLUMN_ID;

    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
        colID: isTreeGroupingColumn ? ('MarketAccount' satisfies keyof CreditBlotterExposure) : undefined,
      }),
      exposure ? getShowJSONContextItem({ params, handleClickJson }) : undefined,
      'separator',
      ...getDefaultContextMenuItems(params),
    ]);
  });

  const dialogs = useMemo(() => <>{jsonModal}</>, [jsonModal]);

  const column = useMemo(
    () =>
      ({
        type: 'hamburgerMenu',
        id: 'rowMenu',
        params: {
          renderItems: params => <HamburgerMenu {...params} onShowJson={handleClickJson} />,
        },
      }) satisfies ColumnDef<Exposure>,
    [handleClickJson]
  );

  return {
    column,
    getContextMenuItems,
    dialogs,
  };
}
